import { type ReactElement } from 'react';
import { useT } from '../i18n/provider';
import { useRouter } from '../router';
import { Badge, Button, PageHeader, Text, CapWidth } from '@vello/web-theme';
import { NavIcon, type NavIconName } from '@vello/web-theme';

/**
 * An honest placeholder for a sidebar destination whose navigation + layout already follow the web-shell standard
 * but whose real data is wired in a later step of the platform-management program (the design's dashed "fora do
 * escopo" frame). It states the section's purpose and shows **no fabricated rows** — consistent with the console's
 * no-fake-data discipline.
 */
const SECTION: Readonly<Record<string, { readonly titleId: string; readonly descId: string; readonly icon: NavIconName }>> = {
  driver_documents: { titleId: 'nav.driverDocuments', descId: 'ph.driverDocuments', icon: 'fileCheck' },
};

export function PlaceholderScreen({ route }: { route: string }): ReactElement {
  const { t } = useT();
  const { navigate } = useRouter();
  const section = SECTION[route] ?? { titleId: 'nav.overview', descId: 'placeholder.note', icon: 'dashboard' as const };
  return (
    <>
      <PageHeader title={t(section.titleId)} subtitle={t(section.descId)} actions={<Badge tone="warning">{t('placeholder.badge')}</Badge>} />
      <div className="vello-placeholder">
        <span className="vello-empty__mark">
          <NavIcon name={section.icon} size={22} />
        </span>
        <Text as="div" role="label">{t('placeholder.title', { section: t(section.titleId) })}</Text>
        <CapWidth size="lg"><Text role="caption" block>{t('placeholder.note')}</Text></CapWidth>
        <Button variant="primary" onClick={() => navigate('dashboard')}>{t('placeholder.back')}</Button>
      </div>
    </>
  );
}
